'use client';
import React from 'react';
import Slider, { Settings } from 'react-slick';
import { TypeModularProductCardFields } from '@/core/generated-types';
import { Document } from '@contentful/rich-text-types';
import classNames from 'classnames';
import {
  Badge,
  HeaderText,
  InternalLink,
  LuvImage,
  RichText,
  UniversalCta,
  DynamicInfoModal,
} from '@/components';

import { HeaderSize } from '@/components/ui/HeaderText';

import useLocale from '@/hooks/useLocale';

import { formatMoney } from '@/utils/helpers';
import { GenericText } from '../GenericText';
import {
  LetterSpacing,
  TextSize,
  TextTransform,
  TextWeight,
} from '../GenericText/GenericText.types';
import { PRODUCT_EVENT_TYPES, trackProductEvent } from '@/utils/rudderstack';
import { ShopifyProduct } from '@/lib/shopify';
import { Entry } from 'contentful';
import { ExtendedProductFields } from '@/core/contentfulConfig';

export interface ModularProductCardProps
  extends Omit<TypeModularProductCardFields, 'product'> {
  product: Entry<ExtendedProductFields>;
  classes?: string;
}

function ModularProductCard(fields: ModularProductCardProps) {
  const {
    leadHeading,
    leadParagraphText,
    title,
    images,
    badge,
    description,
    listHeading,
    listItems,
    overlineText,
    style,
    overlineTextStyle,
    textLink,
    productLink,
    trailingText,
    trailingParagraphText,
    classes,
    product,
    dynamicInfoModal,
    hoverEffect,
    overlineTextCustomSize,
    overlineTextMobileCustomSize,
    titleMobileCustomSize,
    queryString,
  } = fields;

  const shopifyProduct =
    product?.fields?.shopifyProduct || ({} as ShopifyProduct);

  const { price, strikethroughPrice } = product?.fields || {};

  const locale = useLocale();

  const handleCardClick = () => {
    const payload = {
      url: window.location.href,
      productId: shopifyProduct?.id || '',
      name: product?.fields?.title,
      category: shopifyProduct?.productType || '',
      sku: shopifyProduct?.variants?.[0]?.sku || '',
      ageRange: product?.fields?.ageRange || '',
      price: shopifyProduct?.variants?.[0].price?.amount || 0,
      quantity: 1,
    };

    trackProductEvent(PRODUCT_EVENT_TYPES.PRODUCT_CLICKED, payload);
  };

  const LeadingBlock = () => {
    return (
      <>
        {leadHeading && <HeaderText text={leadHeading} size={HeaderSize.H3} />}

        {leadParagraphText && (
          <p className="text-base mb-4 md:mb-6">{leadParagraphText}</p>
        )}
      </>
    );
  };

  const CarouselBlock = () => {
    const baseSettings: Settings = {
      dots: true,
      arrows: false,
      slidesToShow: 1,
      slidesToScroll: 1,
      speed: 500,
    };

    const hasMultipleImages = images.length > 1;

    if (hasMultipleImages) {
      return (
        <div
          data-testid="modularProductCard-carousel"
          className="grid grid-cols-1 luv-carousel product-slider modular-product-slider"
        >
          <Slider {...baseSettings}>
            {images?.map((image, index) => {
              return (
                <LuvImage
                  key={`${image.sys.id}-${index}`}
                  imageData={image}
                  alt={image?.fields?.altText}
                />
              );
            })}
          </Slider>
        </div>
      );
    }

    const featuredImage = images?.[0];

    return (
      <LuvImage
        testId="modularProductCard-featuredImage"
        imageData={featuredImage}
        alt={featuredImage?.fields?.altText}
        className="w-full"
        width={480}
        height={480}
        quality={85}
      />
    );
  };

  const ProductDetailBlock = () => {
    const areCoreElementsPresent = title;

    return (
      <div
        className={classNames({
          'px-4 md:px-6 pb-4 md:pb-6':
            style === 'partiallyContained' && areCoreElementsPresent,
        })}
      >
        <div
          className={classNames({
            'md:mt-6 md:mb-3': areCoreElementsPresent,
          })}
        >
          {badge && <Badge {...badge.fields} />}

          {overlineText && (
            <GenericText
              as="p"
              className={classNames('mt-2 md:mb-2', {
                'font-bradford': overlineTextStyle === 'italics',
              })}
              size={TextSize.SM}
              weight={
                overlineTextStyle === 'caps'
                  ? TextWeight.BOLD
                  : TextWeight.NORMAL
              }
              transform={
                overlineTextStyle === 'caps'
                  ? TextTransform.UPPERCASE
                  : TextTransform.NORMAL
              }
              letterSpacing={
                overlineTextStyle === 'caps' ? LetterSpacing.WIDER : undefined
              }
              customSize={overlineTextCustomSize}
              mobileCustomSize={overlineTextMobileCustomSize}
            >
              {overlineText}
            </GenericText>
          )}

          {title && (
            <GenericText
              as="h2"
              size={TextSize.H2}
              className={classNames('font-semibold md:mb-2', {
                'hover:text-blue-deep-hover hover:underline': !hoverEffect,
              })}
              weight={TextWeight.SEMIBOLD}
              mobileCustomSize={titleMobileCustomSize}
            >
              {title}
            </GenericText>
          )}

          {description && (
            <RichText
              content={description as Document}
              addParagraphMargins={false}
              classes="text-sm md:text-base"
            />
          )}

          {trailingText && <p className="font-bradford">{trailingText}</p>}
        </div>

        {listHeading && (
          <HeaderText text={listHeading} size={HeaderSize.H4} classes="mb-1" />
        )}

        {listItems && <RichText content={listItems as Document} />}

        <div className="flex gap-2 items-center">
          {!!price && !isNaN(price) && (
            <p className="font-bold">{formatMoney(Number(price), locale)}</p>
          )}

          {!!strikethroughPrice && !isNaN(strikethroughPrice) && (
            <p className="line-through font-bold text-grey-light">
              {formatMoney(Number(strikethroughPrice), locale)}
            </p>
          )}
        </div>

        {textLink && (
          <UniversalCta
            link={textLink}
            defaultButtonStyle="Link"
            className="no-underline hover:text-blue-deep-hover mt-2 md:mt-4"
          />
        )}

        {trailingParagraphText && (
          <div className="mt-2 md:mt-3">
            <RichText content={trailingParagraphText as Document} />
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <InternalLink
        link={productLink}
        onClick={handleCardClick}
        classes="inline-block product-card"
        hideArrow
        queryString={queryString}
      >
        <div
          data-testid="modularProductCard"
          className={classNames('text-left grid gap-y-2 lg:gap-y-6', classes, {
            'shadow-lg rounded-md p-4 md:p-6 bg-white':
              style === 'fullyContained',
            'hover:!text-blue-deep': hoverEffect,
          })}
        >
          <LeadingBlock />

          <div
            className={classNames({
              'shadow-lg rounded-md overflow-hidden bg-white':
                style === 'partiallyContained',
            })}
          >
            <CarouselBlock />

            <ProductDetailBlock />
          </div>
        </div>
      </InternalLink>
      <DynamicInfoModal dynamicInfoModal={dynamicInfoModal} />
    </div>
  );
}

export default ModularProductCard;
